import { BackendObjectRef } from './backend-integration.service';

/**
 * List of Form field types known to the editor (general + user fields)
 *
 * This list is taken from `src/lib/forms/general-field.eval.ts#PROVIDE_FIELDS`
 */
export const FormFieldTypeID = {
    boolean       : 'boolean',
    date          : 'date',
    datetime      : 'datetime',
    file          : 'file',
    freetext      : 'freetext',
    heading       : 'heading',
    hidden        : 'hidden',     // What is this one used for?
    likert        : 'likert',     // Unused by EB
    number        : 'number',
    paragraph     : 'paragraph',
    profileField  : 'userField',  // Legacy for user field... Can be removed?
    select        : 'select',
    string        : 'string',
    text          : 'text',
    assignee      : 'assignee',

    // UserField types from `src/lib/forms.ts`
    discrete        : 'discrete',
    discreteMultiple: 'discrete_multiple',
    document        : 'document',
    hierarchy       : 'hierarchy',
    multiselect     : 'multiSelect',
} as const
export type FormFieldTypeID =
    typeof FormFieldTypeID[keyof typeof FormFieldTypeID];

export abstract class FormFieldType implements BackendObjectRef {
    /**
     * Do we support this user field type?
     *
     * The list of the unsupported types here is mainly out of conversation with
     * Matous & Dave. The types here may not work in the editor.
     *
     * @param typeID ID of the user field type from the EB backend
     */
    public static isSupported(_typeID: string): boolean {
        return true;
    }

    public static isValidFormFieldType(type: string|undefined): type is FormFieldTypeID {
        if (type === undefined) { return false; }
        return (Object.values(FormFieldTypeID) as string[]).includes(type);
    }

    /**
     * ID of the particular form field type, this will be a string from the
     * `FormFieldTypeID enum.
     */
    readonly id: FormFieldTypeID|string;

    /**
     * User facing name of the form field type
     */
    readonly name: string;

    /**
     * Form field of this type has distinct set of values user chooses from
     * (eg. select)? Editor shoud provide means to define those.
     */
    public readonly isDiscrete: boolean = false;
    public readonly type?: string;
    public get isAssignee(): boolean { return false; }

    public readonly isInvalid: boolean = false;

    /**
     * Form field of this type is editable (user can input value) when assigned
     * as editable. If this is false fields can't be assigned as editable
     * for example.
     */
    public abstract get isUserEditable(): boolean;

    /**
     * Form field of this type requires some kind of content
     * (eg. paragraph, heading)
     */
    public abstract get isContentRequired(): boolean;

    /**
     * Indication if the field is User field or General field
     */
    public get isUserField(): boolean {
        return this instanceof FormFieldTypeUserField;
    }

    public constructor(id: string, name: string) {
        this.id = id;
        this.name = name;
    }
}

export class FormFieldTypeGeneral extends FormFieldType {
    public readonly isInvalid: boolean;
    public readonly isDiscrete: boolean;

    public get isContentRequired(): boolean {
        if (!FormFieldType.isValidFormFieldType(this.id)) { return false; }

        const contentRequired: FormFieldTypeID[] = [
            FormFieldTypeID.heading,
            FormFieldTypeID.paragraph
        ];
        return contentRequired.includes(this.id);
    }

    public static isSupported(typeID: string): boolean {
        if (!FormFieldType.isValidFormFieldType(typeID)) { return false; }

        const unsupported: FormFieldTypeID[] = [
            FormFieldTypeID.freetext,
            FormFieldTypeID.likert,
            FormFieldTypeID.hidden,
            FormFieldTypeID.profileField
        ];
        return !unsupported.includes(typeID);
    }

    public get isUserEditable(): boolean {
        if (!FormFieldType.isValidFormFieldType(this.id)) { return false; }

        const notEditable: FormFieldTypeID[] = [
            FormFieldTypeID.heading,
            FormFieldTypeID.paragraph
        ];
        return !notEditable.includes(this.id);
    };

    public get isAssignee(): boolean {
        return this.id === FormFieldTypeID.assignee;
    };

    public constructor(id: FormFieldTypeID|string, name: string) {
        super(id, name);

        this.isInvalid = FormFieldType.isValidFormFieldType(id) ? false : true;
        this.isDiscrete = this.id === FormFieldTypeID.select || this.id === FormFieldTypeID.multiselect;
    }
}

export class FormFieldTypeUserField extends FormFieldType {
    public readonly isInvalid: boolean;
    public readonly type?: string;

    private static DiscreteTypes: FormFieldTypeID[] = [
        FormFieldTypeID.discrete,
        FormFieldTypeID.hierarchy,
        FormFieldTypeID.multiselect,
        FormFieldTypeID.select,
    ];

    public readonly availableValues?: BackendObjectRef[];
    public readonly isDiscrete: boolean;
    public get isContentRequired(): boolean { return false; }
    public get isUserEditable(): boolean { return true; }

    public constructor(id: string,
                       name: string,
                       type?: FormFieldTypeID|string,
                       availableValues?: BackendObjectRef[]) {
        super(id, name);

        if (FormFieldType.isValidFormFieldType(type)) {
            this.isInvalid = false;
            this.type = type

            this.isDiscrete = FormFieldTypeUserField.DiscreteTypes.includes(type);
            if (this.isDiscrete) { this.availableValues = availableValues; }
        } else {
            this.isInvalid = true;
            this.isDiscrete = false;
        }
    }
}
